/* Heckin Goods — small progressive enhancements. No dependencies. */
(() => {
  'use strict';

  /* Delivery estimate: recompute dates in the visitor's browser so cached pages stay current. */
  const addBusinessDays = (from, days) => {
    const d = new Date(from.getTime());
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const wd = d.getDay();
      if (wd !== 0 && wd !== 6) added += 1;
    }
    return d;
  };
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  const updateDelivery = (root = document) => {
    root.querySelectorAll('[data-hg-delivery]').forEach((el) => {
      const min = parseInt(el.dataset.minDays, 10) || 6;
      const max = Math.max(parseInt(el.dataset.maxDays, 10) || 12, min);
      const now = new Date();
      const start = el.querySelector('[data-hg-date="start"]');
      const end = el.querySelector('[data-hg-date="end"]');
      if (start) start.textContent = fmt.format(addBusinessDays(now, min));
      if (end) end.textContent = fmt.format(addBusinessDays(now, max));
    });
  };

  /* Size guide: open/close the <dialog>, and move the link next to the matching option label. */
  const initSizeGuides = (root = document) => {
    root.querySelectorAll('[data-hg-size-guide]').forEach((wrap) => {
      const name = (wrap.dataset.optionName || 'Size').trim().toLowerCase();
      const scope = wrap.closest('.product-details') || document;
      const trigger = wrap.querySelector('[data-hg-dialog-open]');
      if (!trigger) return;
      const legends = scope.querySelectorAll('.variant-option legend');
      for (const legend of legends) {
        if (!legend.textContent.trim().toLowerCase().startsWith(name)) continue;
        if (!legend.querySelector('.hg-size-guide__trigger')) {
          /* Horizon re-renders the picker on variant change, so a copy is inserted and re-inserted. */
          const copy = trigger.cloneNode(true);
          legend.classList.add('hg-legend-with-guide');
          legend.appendChild(copy);
        }
        wrap.classList.add('is-attached');
        break;
      }
    });
  };
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-hg-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.hgDialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
      }
      return;
    }
    const closer = event.target.closest('[data-hg-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('hg-dialog')) {
      event.target.close();
    }
  });

  /* FAQ: highlight the topic link for the group in view. */
  const initFaqToc = () => {
    const links = Array.from(document.querySelectorAll('.hg-faq__toc-link'));
    if (!links.length || !('IntersectionObserver' in window)) return;
    const byId = new Map(links.map((a) => [a.getAttribute('href').slice(1), a]));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((a) => a.classList.remove('is-active'));
          byId.get(entry.target.id)?.classList.add('is-active');
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    byId.forEach((_a, id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
  };

  /* Breed tiles: one-row slider with prev/next buttons (touch users just swipe). */
  const initSliders = (root = document) => {
    root.querySelectorAll('[data-hg-slider]').forEach((box) => {
      if (box.dataset.hgSliderReady) return;
      const track = box.querySelector('[data-hg-slider-track]');
      const nav = box.querySelector('[data-hg-slider-nav]');
      if (!track || !nav) return;
      box.dataset.hgSliderReady = '1';
      const prev = nav.querySelector('[data-hg-slider-prev]');
      const next = nav.querySelector('[data-hg-slider-next]');
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        nav.hidden = max <= 4;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max - 4;
      };
      const step = (dir) => track.scrollBy({ left: dir * Math.max(track.clientWidth * 0.8, 160), behavior: 'smooth' });
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const init = (root) => {
    updateDelivery(root);
    initSizeGuides(root);
    initSliders(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init(document);
      initFaqToc();
    });
  } else {
    init(document);
    initFaqToc();
  }

  /* Theme editor + Horizon variant re-renders. */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => init(e.target));
  const mo = new MutationObserver(() => initSizeGuides(document));
  const startObserver = () => {
    const pd = document.querySelector('.product-details');
    if (pd) mo.observe(pd, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver);
  } else {
    startObserver();
  }
})();
